import type { ReactNode } from 'react';

import { cva } from 'cva';
import { Input } from 'react-aria-components/Input';
import {
  SearchField as RACSearchField,
  type SearchFieldProps as RACSearchFieldProps,
  type ValidationResult,
} from 'react-aria-components/SearchField';

import { getPasswordManagerProps } from '../../utils/get_password_manager_props';
import { ClearButton } from '../clear_button/clear_button';
import { FieldDescription } from '../form/field_description';
import { FieldError } from '../form/field_error';
import { FieldLabel } from '../form/field_label';
import { Icon, type IconProps } from '../icon/icon';

import styles from './search_field.module.css';

export const searchFieldVariants = cva({
  base: styles.search_field,
  variants: {
    size: {
      s: styles.size_s,
      m: styles.size_m,
      l: styles.size_l,
    },
    readOnly: {
      true: styles.read_only,
    },
  },
  defaultVariants: {
    size: 'm',
  },
});

export type SearchFieldProps = RACSearchFieldProps & {
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  label?: string;
  placeholder?: string;
  canClear?: boolean;
  size?: 's' | 'm' | 'l';
  tooltip?: ReactNode;
};

type SearchFieldSize = NonNullable<SearchFieldProps['size']>;
const iconSizeMap: Record<SearchFieldSize, IconProps['size']> = {
  s: 'xs',
  m: 's',
  l: 's',
};

/**
 * Wrapper for SearchField component, composed of children as react-aria-components.
 *
 * See also https://react-spectrum.adobe.com/react-aria/SearchField.html
 */

export function SearchField(props: SearchFieldProps) {
  const {
    autoComplete,
    className,
    description,
    errorMessage,
    isDisabled,
    isReadOnly,
    isRequired,
    label,
    canClear,
    size = 'm',
    tooltip,
    ...rest
  } = props;
  const classes = searchFieldVariants({
    size,
    readOnly: isReadOnly,
    className,
  });

  return (
    <RACSearchField
      {...rest}
      autoComplete={autoComplete}
      className={classes}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      isRequired={isRequired}
    >
      {label && (
        <FieldLabel isRequired={isRequired} tooltip={tooltip}>
          {label}
        </FieldLabel>
      )}
      <div className={styles.input_wrapper}>
        <Icon name="search" className={styles.icon} />
        <Input {...getPasswordManagerProps(autoComplete)} />
        {canClear && !isReadOnly && !isDisabled && (
          <ClearButton
            aria-label="Clear search"
            className={styles.clear_button}
            iconSize={iconSizeMap[size]}
          />
        )}
      </div>
      {description && <FieldDescription>{description}</FieldDescription>}
      <FieldError>{errorMessage}</FieldError>
    </RACSearchField>
  );
}
